<template>
    <div class="content-wrapper">
        <app-page-top-section :title="$t('Delivery Log')" />

        <div v-if="loading" class="card card-with-shadow border-0 p-4 text-center text-muted">Loading…</div>

        <div v-else-if="!days.length" class="card card-with-shadow border-0 p-4 text-center text-muted">
            No dispatch records yet.
        </div>

        <div v-for="day in days" :key="day.date" class="card card-with-shadow border-0 mb-4">
            <div class="d-flex justify-content-between align-items-center p-3 border-bottom">
                <h5 class="mb-0">{{ formatDate(day.date) }}</h5>
                <div class="d-flex align-items-center" style="gap:8px;">
                    <label class="mb-0 small">{{ $t('Start') }}</label>
                    <input type="number" min="1" max="8" v-model.number="day.printStart"
                        class="form-control form-control-sm" style="width:60px;" />
                    <button class="btn btn-primary btn-sm" @click="printDay(day)">
                        <app-icon name="printer" class="size-15 mr-1" /> Print
                    </button>
                </div>
            </div>
            <div class="table-responsive">
                <table class="table table-hover mb-0">
                    <thead>
                        <tr>
                            <th>Order ID</th>
                            <th>Patient</th>
                            <th>Practitioner</th>
                            <th>Branch</th>
                            <th>Item</th>
                            <th>Address</th>
                            <th class="text-center">Print</th>
                            <th style="min-width:140px;">Tracking Number</th>
                            <th style="min-width:150px;">Tracking Service</th>
                            <th style="min-width:130px;">Delivery Status</th>
                            <th style="min-width:140px;">Tracking Link</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="rec in day.records" :key="rec.id">
                            <td>{{ rec.order_id }}</td>
                            <td>{{ rec.patient }}</td>
                            <td>{{ rec.practitioner }}</td>
                            <td>{{ rec.branch }}</td>
                            <td>{{ rec.item }}</td>
                            <td style="min-width:160px;">
                                <select v-if="rec.has_address" class="form-control form-control-sm"
                                    v-model="rec.address_ref" @change="changeAddress(rec)">
                                    <option v-for="a in rec.addresses" :key="a.ref" :value="a.ref" :title="a.full">
                                        {{ a.title }}
                                    </option>
                                </select>
                                <span v-else class="text-danger small">No address</span>
                            </td>
                            <td class="text-center">
                                <input type="checkbox" :checked="rec.print" :disabled="!rec.has_address || rec.saving"
                                    @change="togglePrint(rec, $event)" />
                            </td>
                            <td>
                                <input type="text" class="form-control form-control-sm" v-model="rec.tracking_number"
                                    placeholder="—" @blur="saveTracking(rec)" />
                            </td>
                            <td>
                                <select class="form-control form-control-sm" v-model="rec.tracking_service_id"
                                    @change="saveTracking(rec)">
                                    <option :value="null">—</option>
                                    <option v-for="s in deliveryServices" :key="s.id" :value="s.id">{{ s.item_label }}</option>
                                </select>
                            </td>
                            <td>
                                <select class="form-control form-control-sm" v-model="rec.delivery_status"
                                    @change="changeDeliveryStatus(rec)">
                                    <option v-for="o in deliveryStatusOptions" :key="o" :value="o">{{ o }}</option>
                                </select>
                            </td>
                            <td>
                                <div class="d-flex align-items-center">
                                    <input type="text" class="form-control form-control-sm" v-model="rec.tracking_link"
                                        placeholder="URL" @blur="saveTracking(rec)" />
                                    <a v-if="rec.tracking_link" :href="rec.tracking_link" target="_blank" rel="noopener"
                                        class="ml-1" :title="$t('Open link')">
                                        <app-icon name="external-link" class="size-15" />
                                    </a>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import HelperMixin from "../../../../common/Mixin/Global/HelperMixin";
import { axiosGet, axiosPost } from "../../../../common/Helper/AxiosHelper";
import { DELIVERY_LOG_REPORT, DELIVERY_LOG_PRINT, DELIVERY_LOG_ADDRESS, DELIVERY_LOG_PRINT_SHEET, DELIVERY_LOG_TRACKING, DELIVERY_LOG_DELIVERY_STATUS } from "../../../Config/ApiUrl-CP";
import { urlGenerator } from "../../../../common/Helper/AxiosHelper";

export default {
    name: 'app-delivery-log-report',
    mixins: [HelperMixin],
    data() {
        return {
            loading: true,
            days: [],
            deliveryServices: [],
            deliveryStatusOptions: ['Pending', 'Delivered', 'Returned', 'Canceled'],
        };
    },
    created() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;
            axiosGet(DELIVERY_LOG_REPORT).then(res => {
                this.deliveryServices = res.data.delivery_services || [];
                if (Array.isArray(res.data.delivery_status_options) && res.data.delivery_status_options.length) {
                    this.deliveryStatusOptions = res.data.delivery_status_options;
                }
                this.days = (res.data.days || []).map(d => ({
                    ...d,
                    printStart: 1,
                    records: d.records.map(r => ({ ...r, saving: false })),
                }));
            }).catch(() => {
                this.$toastr.e('Could not load the delivery log.');
            }).finally(() => {
                this.loading = false;
            });
        },
        formatDate(value) {
            if (!value) return '-';
            const d = new Date(String(value).replace(' ', 'T'));
            if (isNaN(d.getTime())) return value;
            return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
        },
        togglePrint(rec, event) {
            const val = event.target.checked ? 1 : 0;
            const data = new FormData();
            data.append('id', rec.id);
            data.append('print', val);
            rec.saving = true;
            axiosPost(DELIVERY_LOG_PRINT, data).then(() => {
                rec.print = !!val;
            }).catch(({ response }) => {
                event.target.checked = rec.print;
                this.$toastr.e(response && response.data ? response.data.message : 'Could not update.');
            }).finally(() => {
                rec.saving = false;
            });
        },
        changeAddress(rec) {
            const data = new FormData();
            data.append('id', rec.id);
            data.append('address_ref', rec.address_ref);
            axiosPost(DELIVERY_LOG_ADDRESS, data).then(() => {
                this.$toastr.s('Address updated.');
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Could not update address.');
            });
        },
        printDay(day) {
            const records = day.records.filter(r => r.print && r.has_address);
            if (!records.length) {
                this.$toastr.e('No records marked for printing on this day.');
                return;
            }
            // open the server-rendered A4 label sheet (auto-prints on load)
            let start = parseInt(day.printStart, 10);
            if (!start || start < 1 || start > 8) start = 1;
            const url = urlGenerator(`${DELIVERY_LOG_PRINT_SHEET}?date=${encodeURIComponent(day.date)}&start=${start}`);
            window.open(url, '_blank');
        },
        saveTracking(rec) {
            const data = new FormData();
            data.append('id', rec.id);
            if (rec.tracking_service_id) data.append('tracking_service_id', rec.tracking_service_id);
            if (rec.tracking_number) data.append('tracking_number', rec.tracking_number);
            if (rec.tracking_link) data.append('tracking_link', rec.tracking_link);
            axiosPost(DELIVERY_LOG_TRACKING, data).then(() => {
                this.$toastr.s('Tracking updated.');
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Could not update tracking.');
            });
        },
        changeDeliveryStatus(rec) {
            const data = new FormData();
            data.append('id', rec.id);
            data.append('delivery_status', rec.delivery_status);
            axiosPost(DELIVERY_LOG_DELIVERY_STATUS, data).then(() => {
                this.$toastr.s('Delivery status updated.');
            }).catch(({ response }) => {
                this.$toastr.e(response && response.data ? response.data.message : 'Could not update delivery status.');
            });
        },
    },
};
</script>
